import React, { useState, useEffect } from 'react';
import { adminCheckDepositsAPI } from '../services/api';

const CheckDepositsManagement = () => {
  const [deposits, setDeposits] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    per_page: 20,
    page: 1,
  });
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    amount: '',
    check_number: '',
    status: '',
    rejection_reason: '',
    front_image: null,
    back_image: null,
    front_preview: null,
    back_preview: null,
  });

  useEffect(() => {
    loadStatistics();
    loadDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.status]);

  const loadStatistics = async () => {
    try {
      const res = await adminCheckDepositsAPI.getStatistics();
      setStatistics(res.data || res);
    } catch (e) {
      console.error('Failed to load check deposit statistics:', e);
    }
  };

  const loadDeposits = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        status: filters.status || undefined,
        page: filters.page,
        per_page: filters.per_page,
        search: filters.search || undefined,
      };
      const res = await adminCheckDepositsAPI.getDeposits(params);
      // Handle paginator or plain array
      const data = res?.data?.data || res?.data || res;
      setDeposits(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load check deposits:', e);
      setError('Failed to load check deposits.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }));
  };

  const handleApprove = async (deposit) => {
    if (!window.confirm(`Approve check deposit ${deposit.deposit_id}?`)) {
      return;
    }
    try {
      await adminCheckDepositsAPI.approveDeposit(deposit.id);
      await Promise.all([loadDeposits(), loadStatistics()]);
    } catch (e) {
      console.error('Failed to approve deposit:', e);
      setError(
        e?.response?.data?.message ||
          'Failed to approve deposit. Please try again.',
      );
    }
  };

  const handleReject = async (deposit) => {
    const reason = window.prompt(
      `Enter rejection reason for deposit ${deposit.deposit_id}:`,
    );
    if (!reason) return;

    try {
      await adminCheckDepositsAPI.rejectDeposit(deposit.id, reason);
      await Promise.all([loadDeposits(), loadStatistics()]);
    } catch (e) {
      console.error('Failed to reject deposit:', e);
      setError(
        e?.response?.data?.message ||
          'Failed to reject deposit. Please try again.',
      );
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(amount || 0));

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  const getStatusBadgeClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const startEdit = (deposit) => {
    setEditMode(true);
    setEditForm({
      amount: deposit.amount,
      check_number: deposit.check_number || '',
      status: deposit.status || 'pending',
      rejection_reason: deposit.rejection_reason || '',
      front_image: null,
      back_image: null,
      front_preview: deposit.front_image_url || null,
      back_preview: deposit.back_image_url || null,
    });
  };

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;

      setEditForm((prev) => ({
        ...prev,
        [`${side}_image`]: result,
        [`${side}_preview`]: result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveEdit = async () => {
    if (!selectedDeposit) return;
    try {
      setError('');
      const payload = {
        amount: editForm.amount,
        check_number: editForm.check_number || null,
        status: editForm.status,
        rejection_reason: editForm.rejection_reason || null,
      };
      if (editForm.front_image) {
        payload.front_image = editForm.front_image;
      }
      if (editForm.back_image) {
        payload.back_image = editForm.back_image;
      }
      await adminCheckDepositsAPI.updateDeposit(selectedDeposit.id, payload);
      await Promise.all([loadDeposits(), loadStatistics()]);
      setEditMode(false);
      setSelectedDeposit(null);
    } catch (e) {
      console.error('Failed to update deposit:', e);
      setError(
        e?.response?.data?.message ||
          'Failed to update deposit. Please try again.',
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Check Deposits</h1>
          <p className="mt-1 text-sm text-gray-600">
            Review and manage mobile check deposits submitted by customers.
          </p>
        </div>
      </div>

      {statistics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total Deposits"
            value={statistics.total}
            color="text-blue-600"
          />
          <StatCard
            label="Pending"
            value={statistics.pending}
            color="text-yellow-600"
          />
          <StatCard
            label="Approved"
            value={statistics.approved}
            color="text-green-600"
          />
          <StatCard
            label="Rejected"
            value={statistics.rejected}
            color="text-red-600"
          />
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <select
              value={filters.status}
              onChange={handleStatusFilterChange}
              className="block rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {error && (
            <p className="text-sm text-red-600 truncate max-w-xs">{error}</p>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">
                  Deposit ID
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">
                  Customer
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">
                  Account
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">
                  Amount
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">
                  Status
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">
                  Deposited At
                </th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    Loading check deposits...
                  </td>
                </tr>
              ) : deposits.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No check deposits found.
                  </td>
                </tr>
              ) : (
                deposits.map((deposit) => (
                  <tr
                    key={deposit.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedDeposit(deposit)}
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-gray-900">
                      {deposit.deposit_id}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                      {deposit.user?.name || `User #${deposit.user_id}`}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                      {deposit.account?.account_number || deposit.account_id}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-gray-900">
                      {formatCurrency(deposit.amount)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                          deposit.status,
                        )}`}
                      >
                        {deposit.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                      {formatDateTime(deposit.deposited_at)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right">
                      {deposit.status === 'pending' && (
                        <div className="space-x-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(deposit);
                            }}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReject(deposit);
                            }}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedDeposit && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Deposit Details
            </h2>
            <div className="space-x-2">
              {selectedDeposit && (
                <button
                  type="button"
                  onClick={() => startEdit(selectedDeposit)}
                  className="text-sm text-blue-600 hover:text-blue-800 mr-2"
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSelectedDeposit(null);
                  setEditMode(false);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
          </div>
          {!editMode ? (
            <>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm mb-4">
                <Detail label="Deposit ID" value={selectedDeposit.deposit_id} />
                <Detail label="Status" value={selectedDeposit.status} />
                <Detail
                  label="Amount"
                  value={formatCurrency(selectedDeposit.amount)}
                />
                <Detail
                  label="Check Number"
                  value={selectedDeposit.check_number || '-'}
                />
                <Detail
                  label="Customer"
                  value={
                    selectedDeposit.user?.name ||
                    `User #${selectedDeposit.user_id}`
                  }
                />
                <Detail
                  label="Account"
                  value={
                    selectedDeposit.account?.account_number ||
                    selectedDeposit.account_id
                  }
                />
                <Detail
                  label="Deposited At"
                  value={formatDateTime(selectedDeposit.deposited_at)}
                />
                <Detail
                  label="Processed At"
                  value={formatDateTime(selectedDeposit.processed_at)}
                />
                <Detail
                  label="Rejection Reason"
                  value={selectedDeposit.rejection_reason || '-'}
                />
              </dl>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedDeposit.front_image_url && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Front Image
                    </p>
                    <img
                      src={selectedDeposit.front_image_url}
                      alt="Front of check"
                      className="max-h-40 rounded-md border border-gray-200 object-contain"
                    />
                  </div>
                )}
                {selectedDeposit.back_image_url && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Back Image
                    </p>
                    <img
                      src={selectedDeposit.back_image_url}
                      alt="Back of check"
                      className="max-h-40 rounded-md border border-gray-200 object-contain"
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="amount"
                    value={editForm.amount}
                    onChange={handleEditFieldChange}
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={editForm.status}
                    onChange={handleEditFieldChange}
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Check Number
                  </label>
                  <input
                    type="text"
                    name="check_number"
                    value={editForm.check_number}
                    onChange={handleEditFieldChange}
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Rejection Reason
                  </label>
                  <textarea
                    name="rejection_reason"
                    value={editForm.rejection_reason}
                    onChange={handleEditFieldChange}
                    rows={2}
                    className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Front Image
                  </label>
                  {editForm.front_preview && (
                    <img
                      src={editForm.front_preview}
                      alt="Front preview"
                      className="max-h-32 rounded-md border border-gray-200 object-contain mb-2"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'front')}
                    className="text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Back Image
                  </label>
                  {editForm.back_preview && (
                    <img
                      src={editForm.back_preview}
                      alt="Back preview"
                      className="max-h-32 rounded-md border border-gray-200 object-contain mb-2"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'back')}
                    className="text-xs"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditMode(false);
                    setEditForm({
                      amount: '',
                      check_number: '',
                      status: '',
                      rejection_reason: '',
                      front_image: null,
                      back_image: null,
                      front_preview: null,
                      back_preview: null,
                    });
                  }}
                  className="px-3 py-1.5 rounded-md text-sm text-gray-700 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="px-3 py-1.5 rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
    <p className="text-sm text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${color}`}>{value ?? 0}</p>
  </div>
);

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
    </dt>
    <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
  </div>
);

export default CheckDepositsManagement;


